import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const VerificationHistorySection = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');

  const verificationHistory = [
    {
      id: 'ver-001',
      credentialTitle: 'Software Engineering Certificate',
      verifierName: 'TechCorp HR Department',
      verifierType: 'Corporate',
      timestamp: new Date('2025-09-02T16:30:00'),
      status: 'success',
      method: 'qr_scan',
      ipAddress: '192.168.1.100',
      location: 'San Francisco, CA',
      duration: '2.3s'
    },
    {
      id: 'ver-002',
      credentialTitle: 'Professional Driver License',
      verifierName: 'Ride Share Company',
      verifierType: 'Business',
      timestamp: new Date('2025-09-02T14:15:00'),
      status: 'success',
      method: 'manual_input',
      ipAddress: '10.0.0.45',
      location: 'Los Angeles, CA',
      duration: '1.8s'
    },
    {
      id: 'ver-003',
      credentialTitle: 'Medical Certification',
      verifierName: 'Healthcare Provider',
      verifierType: 'Healthcare',
      timestamp: new Date('2025-09-02T11:45:00'),
      status: 'pending',
      method: 'api_integration',
      ipAddress: '172.16.0.23',
      location: 'New York, NY',
      duration: 'In progress'
    },
    {
      id: 'ver-004',
      credentialTitle: 'Digital Identity Proof',
      verifierName: 'Government Portal',
      verifierType: 'Government',
      timestamp: new Date('2025-09-02T09:20:00'),
      status: 'success',
      method: 'blockchain_verify',
      ipAddress: '203.0.113.42',
      location: 'Washington, DC',
      duration: '3.1s'
    },
    {
      id: 'ver-005',
      credentialTitle: 'Blockchain Developer Badge',
      verifierName: 'Tech Recruitment Agency',
      verifierType: 'Corporate',
      timestamp: new Date('2025-09-01T16:00:00'),
      status: 'failed',
      method: 'qr_scan',
      ipAddress: '198.51.100.15',
      location: 'Austin, TX',
      duration: '0.9s'
    },
    {
      id: 'ver-006',
      credentialTitle: 'Security Clearance Level 2',
      verifierName: 'Defense Contractor',
      verifierType: 'Government',
      timestamp: new Date('2025-09-01T13:30:00'),
      status: 'success',
      method: 'secure_channel',
      ipAddress: '192.0.2.88',
      location: 'Arlington, VA',
      duration: '4.2s'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Verifications', count: verificationHistory?.length },
    { id: 'success', label: 'Successful', count: verificationHistory?.filter(v => v?.status === 'success')?.length },
    { id: 'pending', label: 'Pending', count: verificationHistory?.filter(v => v?.status === 'pending')?.length },
    { id: 'failed', label: 'Failed', count: verificationHistory?.filter(v => v?.status === 'failed')?.length }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-success bg-success/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'failed': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'qr_scan': return 'QrCode';
      case 'manual_input': return 'Keyboard';
      case 'api_integration': return 'Code';
      case 'blockchain_verify': return 'Link';
      case 'secure_channel': return 'Lock';
      default: return 'FileText';
    }
  };

  const getVerifierTypeIcon = (type) => {
    switch (type) {
      case 'Corporate': return 'Building';
      case 'Business': return 'Briefcase';
      case 'Healthcare': return 'Heart';
      case 'Government': return 'Shield';
      default: return 'User';
    }
  };

  const filteredHistory = selectedFilter === 'all' 
    ? verificationHistory 
    : verificationHistory?.filter(item => item?.status === selectedFilter);

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Verification History</h2>
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={16} className="text-primary" />
          <span className="text-sm text-muted-foreground">Real-time tracking</span>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters?.map((filter) => (
          <button
            key={filter?.id}
            onClick={() => setSelectedFilter(filter?.id)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedFilter === filter?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            {filter?.label} ({filter?.count})
          </button>
        ))}
      </div>
      {/* Verification List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {filteredHistory?.map((verification) => (
          <div key={verification?.id} className="border border-border rounded-lg p-4 hover:shadow-sm transition-shadow duration-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${getStatusColor(verification?.status)}`}>
                  <Icon name={getStatusIcon(verification?.status)} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground text-sm mb-1">{verification?.credentialTitle}</h3>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <Icon name={getVerifierTypeIcon(verification?.verifierType)} size={12} />
                    <span>{verification?.verifierName}</span>
                    <span>•</span>
                    <span>{verification?.location}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">{formatTimestamp(verification?.timestamp)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {verification?.timestamp?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
              <div className="flex items-center space-x-2">
                <Icon name={getMethodIcon(verification?.method)} size={12} className="text-muted-foreground" />
                <span className="text-muted-foreground">Method:</span>
                <span className="text-foreground capitalize">{verification?.method?.replace('_', ' ')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Globe" size={12} className="text-muted-foreground" />
                <span className="text-muted-foreground">IP:</span>
                <span className="text-foreground font-mono">{verification?.ipAddress}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Timer" size={12} className="text-muted-foreground" />
                <span className="text-muted-foreground">Duration:</span>
                <span className="text-foreground">{verification?.duration}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Tag" size={12} className="text-muted-foreground" />
                <span className="text-muted-foreground">Type:</span>
                <span className="text-foreground">{verification?.verifierType}</span>
              </div>
            </div>

            {verification?.status === 'failed' && (
              <div className="mt-3 p-2 bg-error/5 border border-error/20 rounded text-xs text-error">
                <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                Verification failed: Invalid signature or expired credential
              </div>
            )}
          </div>
        ))}
      </div>
      {filteredHistory?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No verification history found for the selected filter.</p>
        </div>
      )}
    </div>
  );
};

export default VerificationHistorySection;